/**
 * Parse a barcode scanner string into SKU/GTIN + optional lot + expiry.
 * Supports GS1 (AI 01/10/17), custom delimited (|, ,, ;), and plain SKU barcodes.
 * Lot barcode and SKU barcode are independent payloads — this only extracts what is present.
 */

export type ScannedBarcodeType = 'GS1' | 'CUSTOM' | 'SINGLE_SKU';

export interface ScannedBarcodeParts {
  type: ScannedBarcodeType;
  gtin: string | null;
  batch: string | null;
  /** ISO date YYYY-MM-DD when parseable */
  expiry: string | null;
}

const GS1_GROUP_SEPARATOR = '\x1d';

function yymmddToIso(yymmdd: string): string | null {
  if (!/^\d{6}$/.test(yymmdd)) {
    return null;
  }
  const year = Number(`20${yymmdd.substring(0, 2)}`);
  const month = Number(yymmdd.substring(2, 4));
  const day = Number(yymmdd.substring(4, 6));
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return null;
  }
  return `${year.toString().padStart(4, '0')}-${month
    .toString()
    .padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
}

function parseGs1Barcode(raw: string): ScannedBarcodeParts {
  let str = raw.replace(/\((01|10|17)\)/g, '$1');
  str = str.replace(/\|/g, GS1_GROUP_SEPARATOR);

  const result: ScannedBarcodeParts = {
    type: 'GS1',
    gtin: null,
    batch: null,
    expiry: null,
  };

  const gtinMatch = str.match(/01(\d{14})/);
  if (gtinMatch) {
    result.gtin = gtinMatch[1];
  }

  const expMatch = str.match(/17(\d{6})/);
  if (expMatch) {
    result.expiry = yymmddToIso(expMatch[1]);
  }

  // Lot (AI 10): variable length, ends at GS or end of string.
  const batchMatch = str.match(/10([A-Za-z0-9._\-/]+)(?:\x1d|$)/);
  if (batchMatch) {
    // Avoid swallowing a following fixed AI when FNC1 was stripped.
    let batch = batchMatch[1];
    const clipped = batch.match(/^(.*?)(?=17\d{6}|01\d{14}|21)/);
    if (clipped?.[1]) {
      batch = clipped[1];
    }
    result.batch = batch || null;
  }

  return result;
}

function looksLikeGs1(cleanInput: string): boolean {
  if (/^(?:\x1d|\(01\)|01\d{14})/.test(cleanInput)) {
    return true;
  }
  if (/\(10\)|\(17\)/.test(cleanInput)) {
    return true;
  }
  // Compact GS1 without parentheses: has AI 10 and 17 near numeric payload.
  return /10[A-Za-z0-9._\-/]+/.test(cleanInput) && /17\d{6}/.test(cleanInput);
}

/**
 * Auto-detect barcode payload kind and extract gtin/batch/expiry when present.
 */
export function parseScannedBarcode(rawString: string): ScannedBarcodeParts {
  const cleanInput = (rawString ?? '').trim().replace(/[\r\n]+$/g, '');
  if (!cleanInput) {
    return { type: 'SINGLE_SKU', gtin: null, batch: null, expiry: null };
  }

  if (looksLikeGs1(cleanInput)) {
    return parseGs1Barcode(cleanInput);
  }

  if (/[|,;]/.test(cleanInput)) {
    const parts = cleanInput.split(/[|,;]/).map((part) => part.trim());
    const expiryRaw = parts[2] || null;
    let expiry: string | null = null;
    if (expiryRaw) {
      if (/^\d{6}$/.test(expiryRaw)) {
        expiry = yymmddToIso(expiryRaw);
      } else if (/^\d{4}-\d{2}-\d{2}$/.test(expiryRaw)) {
        expiry = expiryRaw;
      } else if (/^\d{2}\/\d{2}\/\d{4}$/.test(expiryRaw)) {
        const [dd, mm, yyyy] = expiryRaw.split('/');
        expiry = `${yyyy}-${mm}-${dd}`;
      }
    }
    return {
      type: 'CUSTOM',
      gtin: parts[0] || null,
      batch: parts[1] || null,
      expiry,
    };
  }

  return {
    type: 'SINGLE_SKU',
    gtin: cleanInput,
    batch: null,
    expiry: null,
  };
}

/** Convert ISO YYYY-MM-DD to Date at local midnight, or null. */
export function isoDateToLocalDate(iso: string | null | undefined): Date | null {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    return null;
  }
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return Number.isNaN(date.getTime()) ? null : date;
}
